import { PlcData, RawGpsMessage } from '@cco/model';
import { Action, createReducer, on } from '@ngrx/store';
import {
  ProcessGPSDataForTruck,
  SubscribeToGPSForTruck,
  UnsubscribeFromGPSForTruck,
} from './truck-live-data.actions';

export interface LatestTruckData<T> {
  [uuid: string]: {
    subscribe: boolean;
    latest: T;
  };
}

export interface State {
  latestGpsData: LatestTruckData<RawGpsMessage>;
  latestPlcData: LatestTruckData<PlcData>;
}

export const initialState: State = {
  latestGpsData: {},
  latestPlcData: {},
};

const truckLiveDataReducer = createReducer(
  initialState,
  on(SubscribeToGPSForTruck, (state, { truckUuid }): State => {
    const latestGpsData: Partial<LatestTruckData<RawGpsMessage>> = { ...state.latestGpsData };

    if (!latestGpsData[truckUuid]) {
      latestGpsData[truckUuid] = {
        subscribe: true,
        latest: null,
      };
    } else {
      latestGpsData[truckUuid] = { ...latestGpsData[truckUuid], subscribe: true };
    }

    return { ...state, latestGpsData };
  }),
  on(UnsubscribeFromGPSForTruck, (state, { truckUuid }): State => {
    const latestGpsData: Partial<LatestTruckData<RawGpsMessage>> = { ...state.latestGpsData };

    if (!latestGpsData[truckUuid]) {
      latestGpsData[truckUuid] = {
        subscribe: false,
        latest: null,
      };
    } else {
      latestGpsData[truckUuid] = { ...latestGpsData[truckUuid], subscribe: false };
    }

    return { ...state, latestGpsData };
  }),
  on(ProcessGPSDataForTruck, (state, { truckUuid, gpsData }) => {
    const latestGpsData: Partial<LatestTruckData<RawGpsMessage>> = { ...state.latestGpsData };

    gpsData.forEach((gpsDatum) => {
      if (!latestGpsData[truckUuid]) {
        latestGpsData[truckUuid] = {
          subscribe: false,
          latest: null,
        };
      }

      if (
        !latestGpsData[truckUuid].latest ||
        gpsDatum.milliTimestamp > latestGpsData[truckUuid].latest.milliTimestamp
      ) {
        latestGpsData[truckUuid] = { ...latestGpsData[truckUuid], latest: gpsDatum };
      }
    });

    return { ...state, latestGpsData };
  }),
);

export function reducer(state: State | undefined, action: Action) {
  return truckLiveDataReducer(state, action);
}

export const selectLatestGpsData = (state: State) => state.latestGpsData;
export const selectIsLatestGpsDataSubscribedForTruck = (
  state: State,
  props: { truckUuid: string },
) => state.latestGpsData[props.truckUuid] && state.latestGpsData[props.truckUuid].subscribe;
export const selectLatestGpsDataForTruck = (state: State, props: { truckUuid: string }) =>
  state.latestGpsData[props.truckUuid] && state.latestGpsData[props.truckUuid].latest;
